/**
 * Paywall Component
 * Controls access to premium content with Stripe + RevenueCat integration
 *
 * Architecture:
 * 1. Stripe gerencia: Produtos, checkout, pagamento
 * 2. Backend retorna: produtos Stripe e cria sessões checkout
 * 3. RevenueCat sincroniza: Assinatura após Stripe confirmar pagamento
 * 4. Frontend mostra: se usuário tem premium via RevenueCat entitlements
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { useRevenueCatIntegration } from "@/hooks/useRevenueCatIntegration";
import { fetchRevenueCatStatus } from "@/store/slices/subscriptionSlice";
import { useProduct } from "@/hooks/useProduct";
import { PaywallProps, CurrencyInfo } from "@/types/subscription.types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PageLoading } from "@/components/loading";
import {
  Crown,
  CreditCard,
  CheckCircle,
  AlertCircle,
  X,
  ArrowLeft,
} from "lucide-react";
import { logger } from "@/utils/logger";

interface PaywallComponentProps {
  children: React.ReactNode;
  openAccess: boolean;
  backUrl?: string;
  backLabel?: string;
}

const Paywall: React.FC<PaywallComponentProps> = ({
  children,
  openAccess,
  backUrl = "/meeting-simulator",
  backLabel,
}) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { revenueCatLoaded, accessLoaded } = useAppSelector(
    (state) => state.subscription
  );
  const dispatch = useAppDispatch();
  const { hasPremiumAccess, getSubscriptionStatus, isLoading, error } =
    useRevenueCatIntegration();
  const config = useProduct();

  const [products, setProducts] = useState<any[]>([]);
  const [currency, setCurrency] = useState<CurrencyInfo>({
    code: "EUR",
    symbol: "€",
    country: "PT",
  });
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);

  const hasGrantedAccessRef = useRef(false);

  /**
   * Load Stripe products from Azure Function
   */
  const loadProducts = useCallback(async (): Promise<void> => {
    try {
      setIsProductsLoading(true);
      setProductsError(null);

      const azureFunctionsUrl =
        import.meta.env.VITE_AZURE_FUNCTIONS_URL ||
        "https://meeting-gc-functions-v2.azurewebsites.net/api";
      if (!azureFunctionsUrl) {
        throw new Error("Azure Functions URL not configured");
      }

      const url = `${azureFunctionsUrl}/stripe-products`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data?.products || data.products.length === 0) {
        throw new Error("No products available from Stripe");
      }

      setProducts(data.products);
      logger.log(
        "[Paywall] Products loaded from Azure Function:",
        data.products
      );
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to load products";
      setProductsError(errorMessage);
      logger.error("[Paywall] Failed to load products:", error);
    } finally {
      setIsProductsLoading(false);
    }
  }, []);

  /**
   * Detect user currency
   */
  const detectCurrency = async (): Promise<CurrencyInfo> => {
    try {
      // Check localStorage cache first (24h cache)
      const cached = localStorage.getItem("user_currency");
      const cacheTime = localStorage.getItem("currency_cache_time");

      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < 24 * 60 * 60 * 1000) {
          // 24 hours
          return JSON.parse(cached);
        }
      }

      // Fetch from ipapi.co
      const response = await fetch("https://ipapi.co/json/");
      const data = await response.json();

      const currencyMap: Record<string, CurrencyInfo> = {
        BR: { code: "BRL", symbol: "R$", country: "BR" },
        US: { code: "USD", symbol: "$", country: "US" },
        GB: { code: "GBP", symbol: "£", country: "GB" },
        DE: { code: "EUR", symbol: "€", country: "DE" },
        FR: { code: "EUR", symbol: "€", country: "FR" },
        IT: { code: "EUR", symbol: "€", country: "IT" },
        ES: { code: "EUR", symbol: "€", country: "ES" },
        CA: { code: "CAD", symbol: "C$", country: "CA" },
        AU: { code: "AUD", symbol: "A$", country: "AU" },
        JP: { code: "JPY", symbol: "¥", country: "JP" },
      };

      const detectedCurrency = currencyMap[data.country_code] || {
        code: "EUR",
        symbol: "€",
        country: "PT",
      };

      // Cache result
      localStorage.setItem("user_currency", JSON.stringify(detectedCurrency));
      localStorage.setItem("currency_cache_time", Date.now().toString());

      return detectedCurrency;
    } catch (error) {
      logger.error("[Paywall] Currency detection failed:", error);
      return { code: "EUR", symbol: "€", country: "PT" };
    }
  };

  /**
   * Check if user should have access
   * Logic:
   * 1. If module is openAccess=true, everyone gets access
   * 2. If openAccess=false, only premium users (via RevenueCat) get access
   */
  const checkAccess = (): { granted: boolean; reason: string } => {
    // 1. If module is open access, everyone gets it
    if (openAccess) {
      return { granted: true, reason: "open_access" };
    }

    // 2. Check if user is authenticated
    if (!isAuthenticated) {
      return { granted: false, reason: "not_authenticated" };
    }

    // 3. Check loading states
    if (isLoading || getSubscriptionStatus() === "loading") {
      return { granted: false, reason: "loading" };
    }

    // 4. Check premium access via RevenueCat
    if (hasPremiumAccess()) {
      return { granted: true, reason: "premium" };
    }

    // 5. No access - needs premium
    return { granted: false, reason: "no_premium" };
  };

  /**
   * Handle purchase via Stripe Checkout
   *
   * Flow:
   * 1. Backend cria Stripe checkout session
   * 2. Frontend redireciona para Stripe Checkout
   * 3. User completa pagamento no Stripe
   * 4. Stripe webhook notifica backend
   * 5. Backend envia para RevenueCat
   * 6. RevenueCat sincroniza entitlements
   * 7. Paywall re-renderiza e mostra conteúdo premium
   */
  const handlePurchase = async (product: any) => {
    if (!user?.id || !user?.email) {
      logger.error("[Paywall] User not authenticated or missing email");
      return;
    }

    setIsPurchasing(product.id);

    try {
      logger.log("[Paywall] Starting checkout for product:", {
        productId: product.id,
        userEmail: user.email,
        userId: user.id,
      });

      const azureFunctionsUrl =
        import.meta.env.VITE_AZURE_FUNCTIONS_URL ||
        "https://meeting-gc-functions-v2.azurewebsites.net/api";
      if (!azureFunctionsUrl) {
        throw new Error("Azure Functions URL not configured");
      }

      // Chamar Azure Function para criar Stripe checkout session
      const url = `${azureFunctionsUrl}/create-stripe-checkout-session`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          priceId: product.prices[0].id,
          userEmail: user.email,
          userId: user.email, // Use email as RevenueCat app user ID (syncs with Stripe webhooks)
          successUrl: `${window.location.origin}/checkout/success`,
          cancelUrl: `${window.location.origin}/checkout/cancelled`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      logger.log("[Paywall] Checkout session created:", {
        sessionId: data.sessionId,
        hasUrl: !!data.url,
      });

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL in response");
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to create checkout session";

      logger.error("[Paywall] Checkout failed:", {
        message: errorMessage,
        productId: product.id,
        error,
      });
    } finally {
      setIsPurchasing(null);
    }
  }; /**
   * Initialize component - load products and detect currency
   */
  useEffect(() => {
    const initialize = async () => {
      // Detect user currency
      const detectedCurrency = await detectCurrency();
      setCurrency(detectedCurrency);

      // Load Stripe products (no need to wait for RevenueCat initialization)
      await loadProducts();
    };

    initialize();
  }, [loadProducts]);

  // Re-sync RevenueCat when Paywall renders (user may have completed checkout)
  useEffect(() => {
    if (isAuthenticated && revenueCatLoaded) {
      dispatch(fetchRevenueCatStatus(undefined));
    }
  }, [isAuthenticated, revenueCatLoaded, dispatch]);

  // Check access
  const access = checkAccess();
  const shouldShowPaywall = !access.granted && access.reason !== "loading";

  logger.log("[Paywall] Access check:", {
    granted: access.granted,
    reason: access.reason,
    openAccess,
    isAuthenticated,
    isLoading,
    hasPremiumAccess: hasPremiumAccess(),
    isProductsLoading,
    revenueCatLoaded,
    accessLoaded,
  });

  // Prevent flickering by tracking granted access
  if (access.granted && !hasGrantedAccessRef.current) {
    hasGrantedAccessRef.current = true;
  }

  // If access was granted before, keep showing content even during loading
  if (hasGrantedAccessRef.current && access.reason === "loading") {
    return <>{children}</>;
  }

  // Show loading state
  if (access.reason === "loading" || isProductsLoading) {
    return (
      <PageLoading message={config.i18n.paywallComponent.loadingOptions} />
    );
  }

  // Show content if access granted
  if (access.granted) {
    return <>{children}</>;
  }

  // Show paywall
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 p-4">
      <div className="max-w-4xl mx-auto py-8">
        <Button variant="outline" asChild className="mb-6">
          <Link to={backUrl}>
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            {backLabel || "Back"}
          </Link>
        </Button>

        <div className="flex items-center justify-center">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Unlock Premium Features
              </CardTitle>
              <CardDescription>
                Get unlimited access to advanced AI feedback, premium content,
                and priority support
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Error state */}
              {productsError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{productsError}</AlertDescription>
                </Alert>
              )}

              {/* Products */}
              <div className="grid gap-4">
                {products
                  .sort((a, b) => {
                    // Sort by duration using Stripe's interval and interval_count
                    const priceA = a.prices?.[0];
                    const priceB = b.prices?.[0];

                    if (!priceA || !priceB) return 0;

                    const getMonths = (price: any) => {
                      if (price.interval === "year")
                        return price.intervalCount * 12;
                      if (price.interval === "month")
                        return price.intervalCount;
                      return 0;
                    };

                    return getMonths(priceA) - getMonths(priceB);
                  })
                  .map((product) => (
                    <Card key={product.id} className="relative">
                      {/* Annual Badge */}
                      {product.prices?.[0]?.interval === "year" && (
                        <Badge className="absolute -top-2 left-4 bg-green-500 hover:bg-green-600 text-white">
                          <Crown className="h-4 w-4" />
                          &nbsp; Get 2 months free
                        </Badge>
                      )}

                      {/* Quarterly Badge */}
                      {product.prices?.[0]?.interval === "month" &&
                        product.prices?.[0]?.intervalCount === 3 && (
                          <Badge className="absolute -top-2 left-4 bg-green-500 hover:bg-green-600 text-white">
                            <Crown className="h-4 w-4" />
                            &nbsp; 20% off
                          </Badge>
                        )}
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">
                              {product.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {product.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">
                              €
                              {product.prices && product.prices.length > 0
                                ? (product.prices[0].amount / 100).toFixed(2)
                                : "N/A"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {product.prices?.[0]?.interval === "month" &&
                              product.prices?.[0]?.intervalCount === 3
                                ? "every 3 months"
                                : `per ${product.prices?.[0]?.interval || "month"}`}
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={() => handlePurchase(product)}
                          disabled={isPurchasing === product.id}
                          className="w-full"
                          size="lg"
                        >
                          {isPurchasing === product.id ? (
                            "Processing..."
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Subscribe Now
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </div>

              {/* Comparison Table */}
              <div className="space-y-4">
                <h4 className="font-semibold text-center text-lg">
                  Compare Plans
                </h4>
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left p-4"></th>
                        <th className="text-center p-4 font-semibold w-42 min-w-[10.5rem]">
                          Free
                        </th>
                        <th className="text-center p-4 font-semibold w-42 min-w-[10.5rem]">
                          Premium
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm">
                          Daily Stand-Up Simulation
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm">GDPR Compliance</td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm font-medium">
                          50+ Scenarios Simulation
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm font-medium">
                          Unlimited Access
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm font-medium">
                          Priority Support
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                      <tr className="hover:bg-primary/5 transition-colors">
                        <td className="p-4 text-sm font-medium">
                          Progress Tracking
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem]">
                          <X className="w-5 h-5 text-red-500 mx-auto" />
                        </td>
                        <td className="p-4 text-center w-42 min-w-[10.5rem] bg-primary/10">
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  Cancel anytime • No long-term commitment
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
